import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Skeleton,
  Snackbar,
  Container,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  SmartToy as AgentIcon,
  PowerSettingsNew as PowerIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Speed as SpeedIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../constants';
import { agentsService } from '../services/agentsService';

const Agents = () => {
  const navigate = useNavigate();

  // Estados para dados e loading
  const [agents, setAgents] = useState([]);
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para filtros
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Estados para o modal de confirmação de exclusão

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Carregar agentes
  useEffect(() => {
    const loadAgents = async () => {
      try {
        setLoading(true);
        setError(null);
        // Usar lista LEVE para cards
        const summary = await agentsService.getAgentsSummary();
        // Normalizar para os campos usados na UI
        const normalized = summary.map(a => ({
          id: a.id,
          name: a.name || 'Agente',
          description: a.description || '',
          is_active: a.is_active,
          llm_config: { model: a.llm_model },
          tools: a.tools || [],
          conversations: a.conversations || 0,
        }));
        setAgents(normalized);
      } catch (err) {
        console.error('Erro ao carregar agentes:', err);
        setError('Erro ao carregar agentes. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    loadAgents();
  }, []);

  // Filtrar agentes baseado na aba e busca
  useEffect(() => {
    let filtered = agents;

    // Filtrar por status (aba)
    if (tabValue === 1) {
      filtered = filtered.filter(agent => agent.is_active);
    } else if (tabValue === 2) {
      filtered = filtered.filter(agent => !agent.is_active);
    }

    // Filtrar por busca
    if (searchQuery) {
      filtered = filtered.filter(agent =>
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredAgents(filtered);
  }, [agents, tabValue, searchQuery]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleCreateAgent = () => {
    navigate(ROUTES.AGENTS_CREATE);
  };

  const handleEditAgent = (agent) => {
    navigate(`/agents/${agent.id}/edit`);
  };

  const handleToggleAgentStatus = async (agentId, currentStatus) => {
    try {
      await agentsService.toggleAgentStatus(agentId);
      
      // Atualizar o status local
      setAgents(prev => prev.map(agent => 
        agent.id === agentId 
          ? { ...agent, is_active: !currentStatus }
          : agent
      ));
      
      setSnackbar({
        open: true,
        message: `Agente ${!currentStatus ? 'ativado' : 'desativado'} com sucesso!`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao alterar status do agente:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao alterar status do agente. Tente novamente.',
        severity: 'error'
      });
    }
  };



  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleRetry = () => {
    window.location.reload();
  };

  // Função para calcular indicador de performance rápido (sem API call)
  const getQuickPerformanceIndicator = (agent) => {
    const model = agent.llm_config?.model || '';
    const toolsCount = agent.tools_config?.length || 0;
    const timeout = agent.guardrails_config?.timeout || 60;
    
    let score = 100; // Pontuação base
    
    // Penalizar modelos mais lentos
    if (model.includes('gpt-4') && !model.includes('mini')) {
      score -= 30; // GPT-4 é mais lento
    } else if (model.includes('gpt-4o')) {
      score -= 10; // GPT-4o é moderado
    }
    
    // Penalizar muitas ferramentas
    if (toolsCount > 3) {
      score -= (toolsCount - 3) * 15;
    }
    
    // Penalizar timeout muito alto
    if (timeout > 120) {
      score -= 20;
    }
    
    // Determinar categoria e cor
    if (score >= 80) {
      return { 
        label: 'Rápido', 
        color: 'success', 
        icon: <SpeedIcon fontSize="small" /> 
      };
    } else if (score >= 60) {
      return { 
        label: 'Moderado', 
        color: 'warning', 
        icon: <SpeedIcon fontSize="small" /> 
      };
    } else {
      return { 
        label: 'Lento', 
        color: 'error', 
        icon: <SpeedIcon fontSize="small" /> 
      };
    }
  };

  if (error) {
    return (
          <Box sx={{ p: 4 }}>
      <Alert 
        severity="error" 
        action={
          <Button color="inherit" size="small" onClick={handleRetry}>
            Tentar novamente
          </Button>
        }
      >
        {error}
      </Alert>
    </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {/* Controles */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} variant="standard">
            <Tab label="Todos" />
            <Tab label="Ativos" />
            <Tab label="Inativos" />
          </Tabs>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleCreateAgent}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.light',
                color: 'primary.dark',
              },
              borderRadius: 1,
              px: 2,
              py: 0.5,
            }}
          >
            Criar Agente
          </Button>
        </Box>

        {/* Busca */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar agentes..."
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Lista de Agentes */}
      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 3,
        }}
      >
        {loading ? (
          // Skeleton loading
          Array.from({ length: 6 }).map((_, index) => (
            <Card key={`skeleton-${index}`} sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width={100} height={24} />
                  </Box>
                  <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: '12px' }} />
                </Box>
                <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="70%" height={20} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Skeleton variant="text" width={80} height={16} />
                  <Skeleton variant="text" width={80} height={16} />
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: '10px' }} />
                  <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '10px' }} />
                </Box>
              </CardContent>
            </Card>
          ))
        ) : filteredAgents.length === 0 ? (
          // Estado vazio
          <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 8 }}>
            <AgentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {searchQuery ? 'Nenhum agente encontrado' : 'Nenhum agente criado'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchQuery 
                ? 'Tente ajustar sua busca para encontrar agentes' 
                : 'Crie seu primeiro agente para começar a automatizar conversas'
              }
            </Typography>
            {!searchQuery && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateAgent}
                sx={{ borderRadius: 2 }}
              >
                Criar Primeiro Agente
              </Button>
            )}
          </Box>
        ) : (
          // Lista de agentes
          filteredAgents.map((agent, index) => (
            <Card
              key={agent.id}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              sx={{
                height: '180px', // Altura aumentada para melhor espaçamento
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
                },
                transition: 'all 0.3s ease',
                // Aplicar degradê vermelho para agentes inativos
                ...(agent.is_active ? {} : {
                  background: 'linear-gradient(145deg, #ffffff 0%, rgba(244, 67, 54, 0.03) 100%)',
                  border: '1px solid rgba(244, 67, 54, 0.1)',
                  boxShadow: '0 1px 5px rgba(244, 67, 54, 0.08)',
                }),
              }}
            >
              <CardContent sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header compacto do card */}
                <Box sx={{ mb: 1 }}>
                  {/* Nome do agente */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <AgentIcon 
                      color={agent.is_active ? "primary" : "error"}
                      sx={{ 
                        fontSize: 16,
                        ...(agent.is_active ? {} : {
                          filter: 'drop-shadow(0 2px 4px rgba(244, 67, 54, 0.2))',
                        })
                      }}
                    />
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        lineHeight: 1.2,
                        color: agent.is_active ? 'text.primary' : 'error.dark'
                      }}
                    >
                      {agent.name || 'Agente'}
                    </Typography>
                  </Box>
                  
                  {/* Chips compactos de status e performance */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={agent.is_active ? 'Ativo' : 'Inativo'}
                      color={agent.is_active ? 'success' : 'error'}
                      size="small"
                      sx={{ 
                        fontSize: '0.65rem',
                        height: 18,
                        fontWeight: 500,
                        ...(agent.is_active ? {} : {
                          background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
                          color: 'white',
                          fontWeight: 600,
                          boxShadow: '0 2px 4px rgba(244, 67, 54, 0.2)',
                        })
                      }}
                    />
                    {(() => {
                      const perfIndicator = getQuickPerformanceIndicator(agent);
                      return (
                        <Tooltip title={`Performance: ${perfIndicator.label} - baseado no modelo e configurações`}>
                          <Chip
                            icon={perfIndicator.icon}
                            label={perfIndicator.label}
                            color={perfIndicator.color}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.65rem',
                              height: 18,
                              fontWeight: 500,
                              ...(agent.is_active ? {} : {
                                borderColor: 'error.light',
                                color: 'error.dark',
                              })
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </Box>
                </Box>
                
                {/* Descrição compacta com tooltip */}
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography 
                    variant="body2" 
                    color={agent.is_active ? "text.secondary" : "error.main"}
                    sx={{ 
                      fontSize: '0.75rem',
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      opacity: agent.is_active ? 1 : 0.8,
                      flexGrow: 1,
                      mr: 1,
                    }}
                  >
                    {agent.description || 'Agente inteligente para automatizar conversas'}
                  </Typography>

                  {/* Ícone de info com tooltip */}
                  <Tooltip 
                    title={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          {agent.name || 'Agente'}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {agent.description || 'Agente inteligente para automatizar conversas'}
                        </Typography>
                        <Typography variant="caption">
                          Status: {agent.is_active ? 'Ativo' : 'Inativo'}<br/>
                          Conversas: {agent.conversations || 0}<br/>
                          Ferramentas: {agent.tools?.length || 0}<br/>
                          Modelo: {agent.llm_config?.model || 'N/A'}
                        </Typography>
                      </Box>
                    }
                    placement="top"
                  >
                    <IconButton 
                      size="small"
                      sx={{ 
                        color: agent.is_active ? 'text.disabled' : 'error.light',
                        p: 0.5,
                      }}
                    >
                      <InfoIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                {/* Estatísticas e ferramentas compactas */}
                <Box sx={{ mb: 1 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: agent.is_active ? 'text.secondary' : 'error.dark',
                      display: 'block',
                      mb: 0.5
                    }}
                  >
                    {agent.conversations || 0} conversas • {agent.tools?.length || 0} ferramentas
                  </Typography>
                  
                  {/* Ferramentas compactas */}
                  {agent.tools && agent.tools.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {agent.tools.slice(0, 2).map((tool, toolIndex) => (
                        <Chip
                          key={`${agent.id}-tool-${toolIndex}`}
                          label={tool}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem',
                            height: 16,
                            ...(agent.is_active ? {} : {
                              borderColor: 'error.light',
                              color: 'error.dark',
                            })
                          }}
                        />
                      ))}
                      {agent.tools.length > 2 && (
                        <Chip
                          label={`+${agent.tools.length - 2}`}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.65rem',
                            height: 16,
                            ...(agent.is_active ? {} : {
                              borderColor: 'error.light',
                              color: 'error.dark',
                            })
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
                
                {/* Ações compactas */}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 'auto' }}>
                  <Tooltip title="Editar agente">
                    <IconButton
                      onClick={() => handleEditAgent(agent)}
                      color="primary"
                      size="small"
                      sx={{
                        p: 0.5,
                        ...(agent.is_active ? {} : {
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          }
                        })
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={agent.is_active ? 'Desativar agente' : 'Ativar agente'}>
                    <IconButton
                      onClick={() => handleToggleAgentStatus(agent.id, agent.is_active)}
                      color={agent.is_active ? 'success' : 'default'}
                      size="small"
                      sx={{
                        p: 0.5,
                        ...(agent.is_active ? {} : {
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          '&:hover': {
                            backgroundColor: 'rgba(76, 175, 80, 0.2)',
                          }
                        })
                      }}
                    >
                      <PowerIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))
        )}
      </Box>



      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Agents; 